import router from '../../router'
import analytics from '../../api/analytics'
import userService from '../../api/authentication'
import {
  SET_STATUS_MESSAGE,
  SET_PROFILE,
  SET_LOGIN_LOADING,
  SET_USER_PREFERENCES,
  SET_FEATURE_TOGGLE,
} from '../types'
import { saveCookie, getCookie, removeCookie } from '../../utils/auth-utils'
import { toggleService, toggleDefaultData } from '@/api/toggle'
import preferences from '@/api/preferences'
import strings from '@/strings'
import moment from 'moment'
import notify from '../../api/notification'
import { debounce } from 'lodash'

const state = {
  status: null,
  profile: getCookie('profile') || null,
  loginLoading: false,
  userPreferences: getCookie('preferences') || null,
  featureToggle: toggleDefaultData(),
  inactivityTimeout: null,
  inactivityLimit: 20 * 60 * 1000,
  timeRemaining: 20 * 60 * 1000,
  timeRemainingInterval: null,
}

const mutations = {
  [SET_STATUS_MESSAGE](state, status) {
    state.status = status
  },
  [SET_PROFILE](state, profile) {
    state.profile = profile
  },
  [SET_LOGIN_LOADING](state, loading) {
    state.loginLoading = loading
  },
  [SET_USER_PREFERENCES](state, preferences) {
    state.userPreferences = preferences
  },
  [SET_FEATURE_TOGGLE](state, featureToggle) {
    state.featureToggle = featureToggle
  },
  setInactivityTimeout(state, timeout) {
    state.inactivityTimeout = timeout
  },
  clearInactivityTimeout(state) {
    if (state.inactivityTimeout) {
      clearTimeout(state.inactivityTimeout)
      state.inactivityTimeout = null
    }
  },
  setTimeRemaining(state, time) {
    state.timeRemaining = time
  },
  setTimeRemainingInterval(state, interval) {
    state.timeRemainingInterval = interval
  },
  clearTimeRemainingInterval(state) {
    if (state.timeRemainingInterval) {
      clearInterval(state.timeRemainingInterval)
      state.timeRemainingInterval = null
    }
  },
}

const getters = {
  statusMessages: state => {
    return state.status
  },
  toggle: state => {
    return state.featureToggle.feature
  },
  profile: state => {
    return state.profile
  },
  loginLoading: state => {
    return state.loginLoading
  },
  userPreferences: state => {
    return state.userPreferences
  },
  allowExamUrgent: state => {
    return state.profile.laboratoryAllowExamUrgent
  },
  inactivityLimit: state => {
    return state.inactivityLimit
  },
  timeRemainingInMilliseconds: state => {
    return state.timeRemaining
  },
  formattedTimeRemaining: state => {
    if (state.timeRemaining !== null) {
      return moment.utc(state.timeRemaining).format('mm:ss')
    }
    return null
  },
}

const actions = {
  login: function ({ commit, dispatch }, credentials) {
    if (!state.loginLoading) {
      commit(SET_LOGIN_LOADING, true)
      commit(SET_STATUS_MESSAGE, null)
      var returnValue = false
      dispatch('validateCredentials', credentials)
        .then(data => {
          returnValue = true
          saveCookie('token_aol', data.token)
          saveCookie('refresh_token_aol', data.refreshToken)
          dispatch('getUserProfile', data).then(() => {
            router.push('/dashboard')
            commit(SET_LOGIN_LOADING, false)
          })
        })
        .catch(error => {
          returnValue = false
          commit(SET_LOGIN_LOADING, false)
          commit(SET_STATUS_MESSAGE, error.errorCode)

          analytics.pushAnalyticsEvent('ga-event', {
            category: 'Login',
            action: error.errorName,
          })
        })
    }

    return returnValue
  },

  loginSite: function ({ commit, dispatch }, credentials) {
    if (!state.loginLoading) {
      commit(SET_LOGIN_LOADING, true)
      commit(SET_STATUS_MESSAGE, null)
      var returnValue = false
      dispatch('validateCredentials', credentials)
        .then(data => {
          returnValue = true
          saveCookie('token_aol', data.token)
          saveCookie('refresh_token_aol', data.refreshToken)
          dispatch('getUserProfile', data).then(() => {
            router.go()
            setTimeout(() => {
              commit(SET_LOGIN_LOADING, false)
            }, 1000)
          })
        })
        .catch(error => {
          returnValue = false
          commit(SET_LOGIN_LOADING, false)
          commit(SET_STATUS_MESSAGE, error.errorCode)

          analytics.pushAnalyticsEvent('ga-event', {
            category: 'Login Guia de Exames',
            action: error.errorName,
          })
        })
    }

    return returnValue
  },

  /**
   * @argument credentials
   * @returns A promise. If resolved, it sends the auth token,
   * if rejected the payload is an object { errorCode: string, validationError: boolean }
   */
  validateCredentials: function ({ commit }, credentials) {
    return new Promise((resolve, reject) => {
      let valid = true
      let errorCode = ''
      let errorName = ''

      // field validation
      if (
        credentials.password.length < 1 &&
        credentials.username.length < 1 &&
        credentials.idLab.length < 1
      ) {
        errorCode = 'login-missing-both'
        errorName = 'Campos de login não preenchidos'
        valid = false
      } else if (credentials.username.length < 1) {
        errorCode = 'missing-username'
        errorName = 'Campo usuário não preenchido'
        valid = false
      } else if (credentials.password.length < 1) {
        errorCode = 'missing-password'
        errorName = 'Campo senha não preenchido'
        valid = false
      } else if (credentials.idLab.length < 1) {
        errorCode = 'missing-lab'
        errorName = 'Campo ID do Laboratório não preenchido'
        valid = false
      }

      if (!valid) {
        reject({
          errorCode: errorCode,
          errorName: errorName,
          validationError: true,
        })
      } else {
        // server-side validation
        userService
          .newlogin(credentials)
          .then(response => {
            resolve(response.data)
          })
          .catch(err => {
            let isBadCredentials =
              err.response.data && err.response.data.error === 'invalid_grant'

            if (err.response.status === 400 && !isBadCredentials) {
              reject({
                errorCode: 'server-fail',
                errorName: 'Falha no servidor - ' + err.response.status,
                validationError: false,
              })
            }
            if (err.response.data.error_description === 'Inactive user!') {
              reject({
                errorCode: 'login-block',
                errorName: 'Usuário bloqueado',
                validationError: false,
              })
            } else {
              reject({
                errorCode: 'login-fail',
                errorName: 'Usuário e senha inválidos',
                validationError: false,
              })
            }
          })
      }
    })
  },
  logout: function ({ commit, dispatch }) {
    dispatch('clearAllData')
    analytics.pushAnalyticsEvent('ga-event', {
      category: 'Sair',
    })
  },
  clearAllData: function ({ commit, dispatch }) {
    removeCookie('token_aol')
    removeCookie('refresh_token_aol')
    removeCookie('profile')
    removeCookie('toggleAol')
    removeCookie('preferences')
    removeCookie('passwordChanged')
    removeCookie('selectedCollectionPoint')
    toggleService.loaded = false
    commit(SET_STATUS_MESSAGE, null)
    commit(SET_PROFILE, null)
    dispatch('redirectToLogin')
  },
  setStatusMessage: function ({ commit }, message) {
    commit(SET_STATUS_MESSAGE, message)
  },
  getUserProfile: function ({ commit }, data) {
    return new Promise(resolve => {
      let profile = {
        id_laboratory: data.laboratory.id,
        id_operator: data.idOperador,
        username: data.username,
        laboratoryName: data.laboratory.name,
        name: data.name,
        laboratoryCnpj: data.laboratory.cnpj,
        status: data.laboratory.status,
        idGroup: data.laboratory.idGroup,
        laboratoryAllowExamUrgent: data.laboratory.allowExamUrgent,
        laboratoryTypeOutputResult: data.laboratory.typeOutputResult,
        temporaryPassword: data.temporaryPassword,
        lastAccessDate: data.lastAccessDate,
        defaulter: data.laboratory.defaulter,
        idRole: data.idRole,
      }
      saveCookie('profile', profile)
      commit(SET_PROFILE, profile)

      analytics.pushAnalyticsEvent('ga-event', {
        category: 'Login',
        action: 'Sucesso',
      })

      const featureToggle = toggleService.setToggles(data.authorities)
      commit(SET_FEATURE_TOGGLE, featureToggle)
      resolve()
    })
  },
  getUserPreferences: function ({ commit, dispatch }) {
    removeCookie('preferences')
    preferences.loadPreferences().then(preferences => {
      let preferencesResponse
      if (preferences.length > 1) {
        preferencesResponse = preferences
      } else {
        preferencesResponse = strings.defaultPreferences
      }
      saveCookie('preferences', preferencesResponse)
      commit(SET_USER_PREFERENCES, preferencesResponse)
      dispatch('getPreferencesOptions')
    })
  },
  resetInactivityTimeout({ commit, state, dispatch }) {
    commit('clearInactivityTimeout')
    commit('setTimeRemaining', state.inactivityLimit)

    const timeout = setTimeout(() => {
      dispatch('handleInactivity')
    }, state.inactivityLimit)
    commit('setInactivityTimeout', timeout)

    commit('clearTimeRemainingInterval')
    dispatch('startUpdatingTimeRemaining')
  },

  debouncedResetInactivityTimeout({ dispatch }) {
    const dispatHandle = dispatch('resetInactivityTimeout')
    debounce(() => dispatHandle, 1000)
  },

  startUpdatingTimeRemaining({ state, commit }) {
    if (process.env.NODE_ENV === 'production') {
      const interval = setInterval(() => {
        if (state.timeRemaining > 0) {
          commit('setTimeRemaining', state.timeRemaining - 1000)
        } else {
          clearInterval(interval)
        }
      }, 1000)
      commit('setTimeRemainingInterval', interval)
    }
  },

  handleInactivity({ dispatch }) {
    dispatch('logout')
    notify({
      title: 'AVISO:',
      message: 'A sua sessão foi expirada por inatividade.',
    })
  },

  setupInactivityListener({ dispatch }) {
    const debouncedDispatch = dispatch.bind(this, 'debouncedResetInactivityTimeout')
    window.addEventListener('mousemove', debouncedDispatch)
    window.addEventListener('keypress', debouncedDispatch)
    window.addEventListener('scroll', debouncedDispatch)
    window.addEventListener('click', debouncedDispatch)
    dispatch('resetInactivityTimeout')
  },

  removeInactivityListener({ commit }) {
    window.removeEventListener('mousemove', this.debouncedResetInactivityTimeout)
    window.removeEventListener('keypress', this.debouncedResetInactivityTimeout)
    window.removeEventListener('scroll', this.debouncedResetInactivityTimeout)
    window.removeEventListener('click', this.debouncedResetInactivityTimeout)
    commit('clearInactivityTimeout')
    commit('clearTimeRemainingInterval')
  },

  refreshToken: function () {
    userService.refresh().then(response => {
      saveCookie('token_aol', response.data.token)
      saveCookie('refresh_token_aol', response.data.refreshToken)
    })
  },

  redirectToLogin: function () {
    router.replace('/login')
  },
}

export default { state, mutations, actions, getters }
export { mutations, actions, getters }
